import { Badge, Box, Button, HStack, Input, Text, VStack } from '@chakra-ui/react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Pause,
  Play,
  Search,
  Trash2,
} from 'lucide-react'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import type { MqttMessage, Subscription } from '@/lib/mqtt-types'
import { useColorModeValue } from '~/color-mode'
import { PayloadViewer } from './PayloadViewer'

const ITEMS_PER_PAGE = 10

interface MessagesPanelProps {
  messages: MqttMessage[]
  subscriptions: Subscription[]
  isPaused: boolean
  onClear: () => void
  onTogglePause: () => void
  topicFilter?: string | null
}

export const MessagesPanel = memo(function MessagesPanel({
  messages,
  subscriptions,
  isPaused,
  onClear,
  onTogglePause,
  topicFilter,
}: MessagesPanelProps) {
  const [filter, setFilter] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const searchIconColor = useColorModeValue('#777672', '#BBB9B5')

  // Get color for a topic based on subscriptions
  const getTopicColor = (topic: string): string => {
    for (const sub of subscriptions) {
      if (topicMatches(sub.topic, topic)) {
        return sub.color
      }
    }
    return 'content.secondary' // Gray for unknown topics
  }

  // Filter messages by topic filter (from tree) and search filter
  const filteredMessages = useMemo(() => {
    let result = messages

    // Apply topic filter from tree selection
    if (topicFilter) {
      if (topicFilter.endsWith('/')) {
        // Prefix filter (from clicking a folder)
        const prefix = topicFilter.slice(0, -1)
        result = result.filter((msg) => msg.topic.startsWith(prefix))
      } else {
        // Exact topic filter (from clicking a leaf)
        result = result.filter((msg) => msg.topic === topicFilter)
      }
    }

    // Apply search filter
    if (filter.trim()) {
      const lowerFilter = filter.toLowerCase()
      result = result.filter(
        (msg) =>
          msg.topic.toLowerCase().includes(lowerFilter) ||
          msg.payload.toLowerCase().includes(lowerFilter),
      )
    }

    return result
  }, [messages, filter, topicFilter])

  // Pagination calculations
  const totalPages = Math.ceil(filteredMessages.length / ITEMS_PER_PAGE)
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
  const endIndex = startIndex + ITEMS_PER_PAGE
  const paginatedMessages = filteredMessages.slice(startIndex, endIndex)

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [topicFilter, filter])

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)))
  }

  return (
    <Box
      p={4}
      borderWidth="1px"
      borderRadius="lg"
      bg="bg.panel"
      h="100%"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <HStack justify="space-between" mb={4} flexShrink={0}>
        <HStack gap={2}>
          <Text fontWeight="bold">Messages</Text>
          <Badge colorPalette={isPaused ? 'yellow' : 'blue'} variant="subtle">
            {topicFilter ? `${filteredMessages.length}/${messages.length}` : messages.length}
          </Badge>
          {topicFilter && (
            <Badge colorPalette="brand" variant="solid" size="sm">
              Filtered
            </Badge>
          )}
        </HStack>

        <HStack gap={1}>
          <Button
            size="sm"
            variant="ghost"
            onClick={onTogglePause}
            colorPalette={isPaused ? 'green' : 'yellow'}
            title={isPaused ? 'Resume' : 'Pause'}
          >
            {isPaused ? <Play size={16} /> : <Pause size={16} />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClear}
            colorPalette="red"
            title="Clear messages"
          >
            <Trash2 size={16} />
          </Button>
        </HStack>
      </HStack>

      {/* Filter */}
      <Box mb={3} position="relative" flexShrink={0}>
        <Search
          size={14}
          style={{
            position: 'absolute',
            left: 10,
            top: '50%',
            transform: 'translateY(-50%)',
            color: searchIconColor,
          }}
        />
        <Input
          size="sm"
          placeholder="Filter by topic or payload..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          pl={8}
        />
      </Box>

      {/* Messages list */}
      <Box flex={1} overflowY="auto" minH={0}>
        <VStack gap={2} align="stretch">
          {paginatedMessages.length === 0 ? (
            <Box py={8} textAlign="center" color="content.secondary">
              {messages.length === 0 ? (
                <>
                  <Text fontSize="sm">No messages yet</Text>
                  <Text fontSize="xs">Subscribe to topics to start receiving messages</Text>
                </>
              ) : topicFilter ? (
                <>
                  <Text fontSize="sm">No messages for this topic</Text>
                  <Text fontSize="xs">
                    Filtering by: {topicFilter.endsWith('/') ? topicFilter + '*' : topicFilter}
                  </Text>
                  <Text fontSize="xs" mt={1}>
                    Select a different topic or click "Show All" in the Topic Tree
                  </Text>
                </>
              ) : (
                <Text fontSize="sm">No messages match the search filter</Text>
              )}
            </Box>
          ) : (
            paginatedMessages.map((msg) => (
              <Box
                key={msg.id}
                p={3}
                borderRadius="md"
                bg="bg.subtle"
                borderLeftWidth={3}
                borderLeftColor={getTopicColor(msg.topic)}
                cursor="pointer"
                onClick={() => setExpandedId(expandedId === msg.id ? null : msg.id)}
                _hover={{ bg: 'gray.100', _dark: { bg: 'gray.600' } }}
              >
                <HStack justify="space-between" mb={1}>
                  <HStack gap={2} flex={1} minW={0}>
                    <Text
                      fontSize="sm"
                      fontWeight="medium"
                      truncate
                      maxW="100%"
                      color={getTopicColor(msg.topic)}
                    >
                      {msg.topic}
                    </Text>
                    <HStack gap={1} flexShrink={0}>
                      <Badge size="sm" variant="subtle">
                        QoS {msg.qos}
                      </Badge>
                      {msg.retain && (
                        <Badge size="sm" colorPalette="purple" variant="subtle">
                          R
                        </Badge>
                      )}
                    </HStack>
                  </HStack>
                  <Text fontSize="xs" color="content.secondary" flexShrink={0}>
                    {formatTime(msg.timestamp)}
                  </Text>
                </HStack>

                {expandedId === msg.id ? (
                  <Box mt={2}>
                    <PayloadViewer payload={msg.payload} maxHeight="300px" />
                    {msg.userProperties && Object.keys(msg.userProperties).length > 0 && (
                      <Box mt={2} pt={2} borderTopWidth="1px">
                        <Text fontSize="xs" color="content.secondary" mb={1}>
                          User Properties:
                        </Text>
                        <HStack gap={1} flexWrap="wrap">
                          {Object.entries(msg.userProperties).map(([key, value]) => (
                            <Badge key={key} size="sm" variant="outline">
                              {key}: {value}
                            </Badge>
                          ))}
                        </HStack>
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Text fontSize="xs" color="content.secondary" truncate fontFamily="mono">
                    {truncatePayload(msg.payload)}
                  </Text>
                )}
              </Box>
            ))
          )}
        </VStack>
      </Box>

      {/* Pagination */}
      {filteredMessages.length > ITEMS_PER_PAGE && (
        <Box pt={3} borderTopWidth="1px" mt={3} flexShrink={0}>
          <HStack justify="space-between" align="center">
            <Text fontSize="xs" color="content.secondary">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredMessages.length)} of{' '}
              {filteredMessages.length}
            </Text>
            <HStack gap={1}>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
                title="First page"
              >
                <ChevronsLeft size={14} />
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                title="Previous page"
              >
                <ChevronLeft size={14} />
              </Button>
              <Text fontSize="xs" px={2}>
                {currentPage} / {totalPages}
              </Text>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                title="Next page"
              >
                <ChevronRight size={14} />
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages}
                title="Last page"
              >
                <ChevronsRight size={14} />
              </Button>
            </HStack>
          </HStack>
        </Box>
      )}

      {/* Status bar */}
      {isPaused && (
        <Box mt={2} p={2} bg="brand.subtle" borderRadius="md" textAlign="center" flexShrink={0}>
          <Text fontSize="xs" color="brand.fg">
            Message receiving is paused
          </Text>
        </Box>
      )}
    </Box>
  )
})

// Helper function to format time
function formatTime(date: Date): string {
  try {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
      return '--:--:--'
    }
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return '--:--:--'
  }
}

// Helper function to truncate payload
function truncatePayload(payload: string, maxLength = 100): string {
  const trimmed = payload.trim().replace(/\s+/g, ' ')
  if (trimmed.length <= maxLength) return trimmed
  return trimmed.substring(0, maxLength) + '...'
}

// Helper function to check if a topic matches a subscription pattern
function topicMatches(pattern: string, topic: string): boolean {
  const patternParts = pattern.split('/')
  const topicParts = topic.split('/')

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i]
    const topicPart = topicParts[i]

    if (patternPart === '#') {
      return true
    }

    if (patternPart === '+') {
      continue
    }

    if (patternPart !== topicPart) {
      return false
    }
  }

  return patternParts.length === topicParts.length
}

export default MessagesPanel
